import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Cpu, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import Sidebar from '../components/layout/Sidebar';
import { aiUsageService, type AiUsageReport } from '../services/aiUsageService';

/**
 * What the AI costs, split by tier.
 *
 * The question this page exists to answer is whether the free individual tier
 * is affordable and whether $2.99/student/month covers a school student — so
 * FREE vs SCHOOL is the primary split, and per-active-user is the primary
 * number. A grand total tells you nothing you can act on.
 *
 * Tokens are always shown; dollars only when a rate card is configured.
 * Measured and asserted are different things and the page says which is which.
 */

const WINDOWS = [7, 30, 90] as const;

const nf = new Intl.NumberFormat('en-US');
const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
/** Sub-cent per-student figures are the normal case; don't round them to $0.00. */
const usdPrecise = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 });

const TIER_LABEL: Record<string, string> = {
  FREE: 'Free individual',
  SCHOOL: 'School plan',
  SYSTEM: 'Background jobs',
};

const TIER_STYLE: Record<string, string> = {
  FREE: 'bg-amber-100 text-amber-800',
  SCHOOL: 'bg-emerald-100 text-emerald-800',
  SYSTEM: 'bg-slate-100 text-slate-700',
};

export default function AiUsage() {
  const [report, setReport] = useState<AiUsageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<number>(30);

  const load = useCallback(async (window: number) => {
    setLoading(true);
    try {
      setReport(await aiUsageService.get(window));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load AI usage');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  const empty = !loading && report !== null && report.rows.length === 0;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 p-6 lg:p-10">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Cpu className="h-6 w-6 text-indigo-600" />
              <h1 className="text-2xl font-bold text-slate-900">AI usage</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-slate-300 bg-white p-0.5">
                {WINDOWS.map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setDays(w)}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                      days === w ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {w}d
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => void load(days)}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {report && !report.rateCardConfigured && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-900">
                <strong>No rate card configured</strong> — showing tokens only. Set <code className="rounded bg-amber-100 px-1">AI_RATE_CARD</code> to
                per-million-token prices, e.g.{' '}
                <code className="rounded bg-amber-100 px-1">{'{"gpt-4o":{"input":2.5,"output":10}}'}</code>. Prices aren’t hardcoded because a wrong
                number here would get quoted as fact.
              </p>
            </div>
          )}

          {report && report.rateCardConfigured && report.modelsMissingRates.length > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-900">
                No rate for <strong>{report.modelsMissingRates.join(', ')}</strong>. Those rows show tokens only, and any total including them is
                withheld rather than shown low.
              </p>
            </div>
          )}

          {loading && (
            <div className="mt-10 flex items-center justify-center gap-2 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading usage…
            </div>
          )}

          {empty && (
            <div className="mt-10 rounded-xl border border-slate-200 bg-white p-8 text-center">
              <p className="font-semibold text-slate-900">Nothing recorded in this window.</p>
              <p className="mt-1 text-sm text-slate-600">
                Usage is written on every OpenAI call. If this stays empty after someone drafts an achievement with AI, the recording path isn’t
                working — that’s a bug, not a quiet month.
              </p>
            </div>
          )}

          {report && !loading && report.rows.length > 0 && (
            <>
              {/* Per tier — the split the whole table exists for. */}
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {report.totals.byTier.map((t) => {
                  const perUser = t.costUsd !== null && t.activeUsers > 0 ? t.costUsd / t.activeUsers : null;
                  return (
                    <div key={t.tier} className="rounded-xl border border-slate-200 bg-white p-4">
                      <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${TIER_STYLE[t.tier] ?? 'bg-slate-100 text-slate-700'}`}>
                        {TIER_LABEL[t.tier] ?? t.tier}
                      </span>
                      <p className="mt-3 text-2xl font-bold text-slate-900">{t.costUsd !== null ? usd.format(t.costUsd) : `${nf.format(t.totalTokens)} tok`}</p>
                      <p className="mt-1 text-sm text-slate-600">
                        {nf.format(t.calls)} calls
                        {t.activeUsers > 0 && ` · ${nf.format(t.activeUsers)} ${t.activeUsers === 1 ? 'user' : 'users'}`}
                      </p>
                      {perUser !== null && (
                        <p className="mt-2 text-sm font-semibold text-slate-900">
                          {usdPrecise.format(perUser)} <span className="font-normal text-slate-600">per active user</span>
                        </p>
                      )}
                      {t.tier === 'SCHOOL' && perUser !== null && (
                        <p className="mt-1 text-xs text-slate-500">against $2.99/student/month</p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Tier</th>
                      <th className="px-4 py-3">Feature</th>
                      <th className="px-4 py-3">Model</th>
                      <th className="px-4 py-3 text-right">Calls</th>
                      <th className="px-4 py-3 text-right">Tokens</th>
                      <th className="px-4 py-3 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((r) => (
                      <tr key={`${r.tier}|${r.feature}|${r.model}`} className="border-b border-slate-100 last:border-0">
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TIER_STYLE[r.tier] ?? 'bg-slate-100 text-slate-700'}`}>
                            {TIER_LABEL[r.tier] ?? r.tier}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{r.feature}</td>
                        <td className="px-4 py-3 text-slate-600">{r.model}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                          {nf.format(r.calls)}
                          {r.failedCalls > 0 && <span className="ml-1 text-xs font-semibold text-red-600">({nf.format(r.failedCalls)} failed)</span>}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-900">{nf.format(r.totalTokens)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-900">{r.costUsd !== null ? usd.format(r.costUsd) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="mt-3 text-xs text-slate-500">
                Since {new Date(report.from).toLocaleDateString()} · {nf.format(report.totals.calls)} calls ·{' '}
                {nf.format(report.totals.totalTokens)} tokens
                {report.totals.costUsd !== null && ` · ${usd.format(report.totals.costUsd)}`}
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
